const DEFAULT_TIMEOUT_MS = 60000;

export function timeRangeToIso(timeRange, nowMs = Date.now()) {
  if (typeof timeRange === 'object' && timeRange?.type === 'custom') {
    return {
      from: new Date(timeRange.from).toISOString(),
      to: new Date(timeRange.to).toISOString(),
    };
  }

  const durations = {
    '1d': 86400000,
    '24h': 86400000,
    '7d': 604800000,
    '30d': 2592000000,
  };
  const duration = durations[timeRange] || durations['1d'];
  return {
    from: new Date(nowMs - duration).toISOString(),
    to: new Date(nowMs).toISOString(),
  };
}

export function buildAnalyticsParams({ timeRange, tag, systemIds = [], filters = {}, page, pageSize }, nowMs) {
  const { from, to } = timeRangeToIso(timeRange, nowMs);
  const params = new URLSearchParams({ from, to });

  if (tag) params.set('tag', tag);
  systemIds.filter(Boolean).forEach((systemId) => params.append('systemIds', systemId));
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      params.set(key, String(value).trim());
    }
  });
  if (page != null) params.set('page', String(page));
  if (pageSize != null) params.set('pageSize', String(pageSize));
  return params;
}

class RawTelemetryAnalyticsService {
  constructor() {
    const host = (process.env.REACT_APP_WBC_API_BASE_URL || 'http://localhost:8080').replace(/\/$/, '');
    const queryPathRaw = process.env.REACT_APP_WBC_QUERY_SERVICE_PATH || '/queryService/api/v1';
    const queryPath = queryPathRaw.startsWith('/') ? queryPathRaw : `/${queryPathRaw}`;
    this.baseUrl = `${host}${queryPath}/analytics`;
    this.timeoutMs = parseInt(process.env.REACT_APP_PANEL_API_TIMEOUT, 10) || DEFAULT_TIMEOUT_MS;
  }

  async request(path, options) {
    const params = buildAnalyticsParams(options);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}${path}?${params.toString()}`, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(payload.error || `Request failed with HTTP ${response.status}`);
        error.sourceStatus = payload.sourceStatus || 'ERROR';
        error.status = response.status;
        throw error;
      }
      return payload;
    } catch (error) {
      if (error.name === 'AbortError') {
        const timeoutError = new Error('The analytics request timed out.');
        timeoutError.sourceStatus = 'TIMEOUT';
        throw timeoutError;
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  getDataStallSummary(options) {
    return this.request('/data-stalls/summary', options);
  }

  getDataStallEvents(options) {
    return this.request('/data-stalls/events', options);
  }

  getSubsystemRestartSummary(options) {
    return this.request('/subsystem-restarts/summary', options);
  }

  getSubsystemRestartEvents(options) {
    return this.request('/subsystem-restarts/events', options);
  }
}

const rawTelemetryAnalyticsService = new RawTelemetryAnalyticsService();
export default rawTelemetryAnalyticsService;
